'use client';

import { useSessionStorageState } from 'ahooks';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineShare } from '@/app/components/icons/BaseLineShare';
import { ActivityStatus } from '@/app/components/shared/Activity/ActivityStatus';
import { openAlertDialog } from '@/app/components/shared/imperative-dialog';
import { useRenderFormat } from '@/app/components/shared/NotificationCenter/hook/use-render-format';
import { ReferenceModal } from '@/app/components/shared/ReferenceModal';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { ACTIVITY_PAGE_FROM_KEY, HOME_PAGE } from '@/app/constants';
import { useCopy, useFetch, useGlobalModal } from '@/app/hooks';
import { type Activity, ActivityPageFromEnum, ActivityType } from '@/app/types';
import { cn, formatDate, reportEvent } from '@/app/utils';
import { PostWorksContainer } from './_components/PostWorksContainer';
import { SubmitButton } from './_components/SubmitButton';
import { getActivity, signUpRewardActivity } from './apis';

interface ActivityPageProps {
  activityId: string;
}

const ExpandButton = memo(function ExpandButton({
  isExpanded,
  onClick,
  expandLabel,
  collapseLabel,
}: {
  isExpanded: boolean;
  onClick: () => void;
  expandLabel: string;
  collapseLabel: string;
}) {
  return (
    <div
      className="-translate-x-1/2 absolute bottom-10 left-1/2 z-10 inline-flex h-8 w-20 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg bg-[#191D25] px-5 transition-all duration-200 [background:linear-gradient(0deg,rgba(255,255,255,0.10),rgba(255,255,255,0.10)),#191D25] [&:hover]:bg-[#191D25] [&:hover]:bg-[linear-gradient(0deg,rgba(255,255,255,0.24),rgba(255,255,255,0.24)),#191D25]"
      onClick={onClick}
    >
      <div className="inline-flex items-center justify-center gap-2 border-0">
        <div className="relative justify-start text-center font-normal text-sm text-white leading-tight">
          {isExpanded ? collapseLabel : expandLabel}
        </div>
      </div>
    </div>
  );
});

const ActivityContent = memo(function ActivityContent({
  content,
  contentRef,
  isExpanded,
  shouldShowExpand,
  onToggleExpand,
  expandLabel,
  collapseLabel,
}: {
  content: string;
  contentRef: React.RefObject<HTMLDivElement | null>;
  isExpanded: boolean;
  shouldShowExpand: boolean;
  onToggleExpand: () => void;
  expandLabel: string;
  collapseLabel: string;
}) {
  const { renderHtmlContent } = useRenderFormat();

  return (
    <div className="relative w-full">
      <div className={cn('mt-2 w-full', isExpanded ? 'pb-[88px]' : 'pb-6')}>
        <div
          ref={contentRef}
          className={cn(
            'prose relative prose-img:my-4 prose-video:my-4 whitespace-pre-wrap break-words prose-img:rounded-lg prose-video:rounded-lg font-normal prose-strong:font-medium prose-a:text-blue-400 prose-p:text-white/90 prose-strong:text-white text-sm text-white leading-snug prose-a:no-underline hover:prose-a:underline [&_video]:bg-black/20',
            !isExpanded && shouldShowExpand ? 'max-h-[400px] overflow-hidden' : 'max-h-full',
          )}
        >
          {renderHtmlContent(content)}
        </div>

        {shouldShowExpand && (
          <>
            <div
              className={cn(
                'absolute bottom-6 left-0 z-10 h-[170px] w-full bg-[linear-gradient(0deg,#020B13_0%,rgba(2,11,19,0)_100%)]',
                isExpanded && 'hidden',
              )}
            />

            <ExpandButton
              isExpanded={isExpanded}
              onClick={onToggleExpand}
              expandLabel={expandLabel}
              collapseLabel={collapseLabel}
            />
          </>
        )}
      </div>
    </div>
  );
});

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);

    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [query]);

  return matches;
};

const formatCountdown = (
  endTime: string | undefined,
  now: number,
  formatLabel: (days: number, hours: number) => string,
  fallbackLabel: string,
) => {
  if (!endTime) return fallbackLabel;
  const remaining = Math.max(0, new Date(endTime).getTime() - now);
  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  return formatLabel(days, hours);
};

const REWARD_ACTIVITY_SUBMIT_ENTRY = 'activity_detail_again';

const RewardSignUpSuccessDialog = ({
  open,
  onClose,
  onStartCreate,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onStartCreate: () => void;
  onSubmit: () => void;
}) => {
  const t = useTranslations();
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-[560px] rounded-16 border-white/10 bg-[#0B1118] p-6">
        <div className="space-y-4">
          <div className="font-semibold text-lg text-white">{t('activity.reward.signUpSuccessTitle')}</div>
          <div className="text-sm text-white/70">{t('activity.reward.signUpSuccessDescription')}</div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={onStartCreate}>
              {t('activity.reward.startCreate')}
            </Button>
            <Button variant="primary" onClick={onSubmit}>
              {t('activity.reward.postNow')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SCROLL_ELEMENT_ID = 'scrollableDivForActivity';

export const ActivityPageClient = ({ activityId }: ActivityPageProps) => {
  const t = useTranslations();
  const router = useRouter();
  const { copy } = useCopy();

  const [activity, setActivity] = useState<Activity | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [shouldShowExpand, setShouldShowExpand] = useState(false);
  const [isRewardSignedUp, setIsRewardSignedUp] = useState(false);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const contentRef = useRef<HTMLDivElement>(null);
  const activityPageViewIdRef = useRef('');
  const rewardActionButtonShowKeyRef = useRef('');
  const rewardInfoShowActivityIdRef = useRef('');
  const isLargerThanMd = useMediaQuery('(min-width: 1024px)');

  const { open: openPostsModal } = useGlobalModal('postsModal');

  const [activityPageFrom, setActivityPageFrom] = useSessionStorageState<ActivityPageFromEnum>(ACTIVITY_PAGE_FROM_KEY, {
    defaultValue: ActivityPageFromEnum.OUTSIDE,
  });

  useFetch(() => getActivity(activityId), {
    ready: !!activityId,
    refreshDeps: [activityId],
    onSuccess: ({ data }) => {
      if (!data?.link?.valid) {
        router.replace(HOME_PAGE);
        return;
      }

      setActivity(data as Activity);
      setIsRewardSignedUp(!!data.signed_up);
    },
  });

  const { run: signupRewardActivity, loading: signingUpRewardActivity } = useFetch(
    () => signUpRewardActivity(activity?.id || ''),
    {
      manual: true,
      onSuccess: ({ error }) => {
        if (error) {
          toast({
            type: 'error',
            message: error.message || t('popup.dialog.posts.failed'),
          });
          return;
        }

        setIsRewardSignedUp(true);
        reportEvent('signup_result', {
          activity_id: activity?.id || '',
          free_credits: 0,
          credit_expire_time: '',
        });
        setRewardDialogOpen(true);
      },
    },
  );

  useEffect(() => {
    reportEvent('banner_page_show', {
      activity_id: activityId,
      from_page: activityPageFrom || ActivityPageFromEnum.OUTSIDE,
    });
  }, [activityPageFrom, activityId]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowTick(Date.now());
    }, 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useLayoutEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setShouldShowExpand(contentHeight > 325);
    }
  }, [activity?.content]);

  const handleBack = () => {
    setActivityPageFrom(ActivityPageFromEnum.OUTSIDE);
    router.back();
  };

  const handleToggleExpand = useCallback(() => {
    reportEvent('detail_expand_click', {
      activity_id: activityId,
    });
    setIsExpanded((prev) => !prev);
  }, [activityId]);

  const handleShareClick = () => {
    copy(window.location.href, { hideMessage: true });
    openAlertDialog({
      closable: false,
      description: t('create.share.description'),
      buttonText: t('elements.deleteElement.rightBtn'),
    });
  };

  const isCompetition = activity?.type === ActivityType.Competition;
  const isRewardActivity = activity?.type === ActivityType.Reward;
  const isRewardInProgress =
    isRewardActivity && (activity?.phase === 'in_progress' || activity?.phase === 'in_submission');
  const isRewardButtonEnabled = isRewardInProgress && !signingUpRewardActivity;
  const shouldShowWorks = activity?.phase !== 'not_started' && activity?.type !== ActivityType.Regular;
  const isEnded = activity?.phase === 'completed';

  const handleRewardSignup = async () => {
    if (!activity) return;
    reportEvent('action_button_click', {
      activity_id: activity.id || '',
      button_type: 'signup',
      user_status: isRewardSignedUp ? '已报名' : '未报名',
    });
    signupRewardActivity();
  };

  const handleRewardStartCreate = () => {
    const redirectUrl = activity?.link?.redirect_url?.trim() || '';
    // 站内路径（/xxx，排除 //host 这类 protocol-relative 外链）
    const isInternalPath = redirectUrl.startsWith('/') && !redirectUrl.startsWith('//');
    const isHttpUrl = /^https?:\/\//i.test(redirectUrl);
    const target = isInternalPath || isHttpUrl ? redirectUrl : '/create/tools';

    reportEvent('start_creation_click', {
      activity_id: activity?.id || '',
      redirect_tool_url: target,
    });
    setRewardDialogOpen(false);

    if (isHttpUrl) {
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }
    // 站内路径或兜底 /create/tools
    router.push(target);
  };

  const openRewardPostsModal = () => {
    if (!activity) return;
    reportEvent('submit_entry_click', {
      activity_id: activity.id || '',
      entry_type: REWARD_ACTIVITY_SUBMIT_ENTRY,
      user_signup_status: isRewardSignedUp ? '已报名' : '未报名',
    });
    setRewardDialogOpen(false);
    openPostsModal({
      regularActivity: activity,
      isActivityLocked: true,
      showMediaLinkInput: true,
      hidePostTutorial: true,
      hidePostReference: true,
      entryType: REWARD_ACTIVITY_SUBMIT_ENTRY,
    });
  };

  const handleRewardPostNow = () => {
    if (!activity) return;
    reportEvent('action_button_click', {
      activity_id: activity.id || '',
      button_type: 'submit',
      user_status: isRewardSignedUp ? '已报名' : '未报名',
    });
    openRewardPostsModal();
  };

  const handleRewardDialogSubmit = () => {
    if (!activity) return;
    reportEvent('one_click_submit_click', {
      activity_id: activity.id || '',
    });
    openRewardPostsModal();
  };

  const rewardButtonText = !isRewardInProgress
    ? isEnded
      ? t('activity.reward.ended')
      : activity?.phase && activity.phase !== 'unspecified'
        ? t(`activity.status.${activity.phase}`)
        : t('activity.reward.ended')
    : isRewardSignedUp
      ? t('activity.reward.post')
      : t('activity.reward.signUp');
  const canShowRewardCountdown = isRewardActivity && activity?.phase === 'in_submission';
  const rewardCountdownText = canShowRewardCountdown
    ? formatCountdown(
        activity?.end_time,
        nowTick,
        (days, hours) => t('activity.reward.countdown', { days, hours }),
        t('activity.reward.countdownFallback'),
      )
    : '';

  useEffect(() => {
    if (!activity || !isRewardActivity) return;
    if (activityPageViewIdRef.current === activity.id) return;
    activityPageViewIdRef.current = activity.id || '';
    reportEvent('activity_page_view', {
      activity_id: activity.id || '',
      activity_name: activity.title || '',
      user_status: isRewardSignedUp ? '已报名' : '未报名',
      entry_source: activityPageFrom || ActivityPageFromEnum.OUTSIDE,
    });
  }, [activity, isRewardActivity, isRewardSignedUp, activityPageFrom]);

  useEffect(() => {
    if (!activity || !isRewardActivity || !rewardCountdownText) return;
    if (rewardInfoShowActivityIdRef.current === activity.id) return;
    rewardInfoShowActivityIdRef.current = activity.id || '';
    reportEvent('activity_info_show', {
      activity_id: activity.id || '',
      deadline_remaining: rewardCountdownText,
      activity_status: activity.phase || '',
    });
  }, [activity, isRewardActivity, rewardCountdownText]);

  useEffect(() => {
    if (!activity || !isRewardActivity) return;
    const buttonType = !isRewardInProgress ? 'disabled' : isRewardSignedUp ? 'submit' : 'signup';
    const showKey = `${activity.id}-${buttonType}-${rewardButtonText}`;
    if (rewardActionButtonShowKeyRef.current === showKey) return;
    rewardActionButtonShowKeyRef.current = showKey;
    reportEvent('action_button_show', {
      activity_id: activity.id || '',
      button_type: buttonType,
      button_text: rewardButtonText,
    });
  }, [activity, isRewardActivity, isRewardInProgress, isRewardSignedUp, rewardButtonText]);

  useEffect(() => {
    if (!rewardDialogOpen || !activity) return;
    reportEvent('signup_success_dialog_show', {
      activity_id: activity.id || '',
      free_credits: 0,
    });
  }, [rewardDialogOpen, activity]);

  if (!activity) {
    return (
      <div className="relative size-full bg-[#020B13]">
        <div className="flex h-[200px] w-full items-center justify-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="relative size-full bg-[#020B13]">
      {/* 返回按钮 */}
      <span
        className="absolute top-8 left-8 z-30 flex size-9 cursor-pointer items-center justify-center rounded-8 border border-system-white10 bg-system-white10 text-system-white transition-all duration-300 hover:bg-system-white20 md:top-4 md:left-4"
        onClick={handleBack}
      >
        <BaseLineLeft className="size-5" />
      </span>

      {/* 主内容区域 */}
      <div
        id={SCROLL_ELEMENT_ID}
        className="mx-auto flex h-full w-full flex-1 flex-col items-center overflow-y-auto rounded-8 px-[10%] md:px-6 md:pb-[var(--ios-safe-bottom)]"
        style={{ scrollbarGutter: 'stable' }}
      >
        {/* 活动标题以及活动状态 */}
        <div className="sticky top-0 z-20 flex w-full flex-col items-start gap-2 bg-[#020B13] pt-8 pb-3 md:pt-[68px]">
          <div className="flex w-full items-center justify-between font-semibold text-[1.75rem] text-white leading-[140%] md:text-5">
            <h1 className="flex-1 truncate pr-2">{activity.title}</h1>
            <div className="flex gap-3">
              <Button className="h-10 gap-2 rounded-10 bg-[#333A45] px-6" onClick={handleShareClick} size="sm">
                <BaseLineShare className="size-5" />
                {t('task.action.share')}
              </Button>
              {isCompetition && (
                <SubmitButton
                  onClick={() => {
                    openPostsModal({
                      regularActivity: activity,
                      isActivityLocked: true,
                      showMediaLinkInput: true,
                      hidePostTutorial: true,
                      hidePostReference: true,
                    });
                  }}
                  activity={activity}
                  isFixed={!isLargerThanMd}
                />
              )}
              {isRewardActivity && (
                <Button
                  variant={isRewardButtonEnabled ? 'primary' : 'outline'}
                  disabled={!isRewardButtonEnabled}
                  onClick={isRewardSignedUp ? handleRewardPostNow : handleRewardSignup}
                >
                  {rewardButtonText}
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ActivityStatus status={activity.phase} />
            <div className="text-white text-xs leading-[140%] opacity-[0.64]">
              {t('activity.detail.activityTime', {
                start: formatDate(activity.start_time || ''),
                end: formatDate(activity.end_time || ''),
              })}
            </div>
          </div>
          {isRewardActivity && rewardCountdownText && (
            <div className="text-sm text-white/60">
              <div>{rewardCountdownText}</div>
            </div>
          )}
        </div>

        <div className="flex w-full flex-col items-center">
          {/* 活动内容区域 */}
          <ActivityContent
            content={activity.content || ''}
            contentRef={contentRef}
            isExpanded={isExpanded}
            shouldShowExpand={shouldShowExpand}
            onToggleExpand={handleToggleExpand}
            expandLabel={t('activity.detail.expand')}
            collapseLabel={t('activity.detail.collapse')}
          />

          {shouldShowWorks && (
            <div className="w-full pb-12">
              <PostWorksContainer activity={activity} isContentExpanded={isExpanded} />
            </div>
          )}
        </div>
      </div>

      {isRewardActivity && (
        <RewardSignUpSuccessDialog
          open={rewardDialogOpen}
          onClose={() => setRewardDialogOpen(false)}
          onStartCreate={handleRewardStartCreate}
          onSubmit={handleRewardDialogSubmit}
        />
      )}
      {/* 主体弹窗 */}
      <ReferenceModal />
    </div>
  );
};
